import {
  PostEditorAction,
  PostEditorMediaAddFromLibAction,
  PostEditorMediaAddFromUploadAction,
  PostEditorMediaFailedUploadAction,
  PostEditorMediaRemoveAction,
  PostEditorMediaReorderAction,
  PostEditorMediaUpdateAction,
  PostEditorMediaUpdateDeferredLoadedAction,
  PostEditorMediaVaryAction,
  PostEditorReducerFunction
} from '../PostEditorActions';
import {
  GroupPostAttributesType,
  MediaTypePostEditorState,
  PostEditorErrors,
  PostGroupsType,
  VariationMeta
} from '../../../../models/post-editor';
import { PostEditorErrorKeys } from '../../../../models/validator-props';
import { Dispatch } from 'react';
import { OnUploadStatusChangeCallbackType } from '../../../../hooks/useMediaUpload';
import { MediaUploadStatus } from '../../../../constants/media';
import { MediaLibraryHelpers } from '../../../Publish/MediaLibrary/mediaLibraryHelpers';
import { showToast } from '../../../../helpers/showToast';
import { MediaItem, MediaItemUpload } from '../../../../models/media';
import { getMediaProportion } from '../../../../helpers/media';
import {
  OnStateUpdateCallbackFunction,
  postGroupCompleteUpdater,
  postGroupStateUpdater
} from '../../helpers/postGroupStateUpdater';
import { ImmutabilityContext } from '../effects/customImmutabilityContext';
import { calculateVariations } from '../../../../helpers/calculateVariations';
import { validatorStateUpdater } from '../../validators/helpers/validatorStateUpdater';
import { ErrorMessageSeverity } from '../../../../constants/form';
import { validator } from '../../validators/helpers/validator';
import { PostEditorGroupIndexProps } from '../PostEditorGroupDataProvider';
import { ActiveCustomizeIndexType } from '../ActiveCustomizeIndexProvider';
import { BulkAddExternalPhotosMutation, BulkCompleteMediaUploadsMutation, Photo } from '../../../../models/graphql';
import { ErrorMessageUploadFromURL } from '../../ErrorMessageUploadFromUrl';
import { BeaconType } from '../../../../models/Beacon';
import { _getApolloClient } from '../../../../apollo/ApolloConfig';

export const onUploadStatusChangeFunction =
  (
    dispatch: Dispatch<PostEditorAction>,
    selectedWorkspaceId: string,
    context: PostEditorMediaUpdateAction['payload']['context'],
    postGroupIndex: PostEditorGroupIndexProps['postGroupIndex'],
    customizeIndex: ActiveCustomizeIndexType,
    beacon: BeaconType
  ): OnUploadStatusChangeCallbackType =>
  (uploadItem, status) => {
    if (status === MediaUploadStatus.PENDING) {
      const media = mapMediaUploadItemToState(uploadItem);
      dispatch({ type: 'mediaAddFromUpload', payload: { customizeIndex, postGroupIndex, media } });
    } else if (status === MediaUploadStatus.COMPLETED && uploadItem.completedMediaItem) {
      dispatch({ type: 'mediaUpdate', payload: { media: uploadItem, context } });
      MediaLibraryHelpers.addItemToMediaLibraryCache(
        uploadItem.id,
        status,
        uploadItem.completedMediaItem,
        selectedWorkspaceId,
        _getApolloClient()
      );
    } else if (status === MediaUploadStatus.FAILED) {
      if (uploadItem.url) {
        showToast({
          severity: 'error',
          summary: 'Unable to Fetch Image',
          detail: ErrorMessageUploadFromURL(uploadItem.url, beacon)
        });
      } else {
        showToast({
          severity: 'error',
          detail: `Item ${
            uploadItem.file?.name || uploadItem.url
          } could not be uploaded. Please check your file and try again.`,
          summary: 'Upload Failed'
        });
      }
      if (!uploadItem.id) return;
      const { id, isGif } = uploadItem;
      const payload: PostEditorMediaFailedUploadAction['payload'] = {
        id,
        gif: isGif,
        type: uploadItem.type
      };
      dispatch({ type: 'mediaFailedUpload', payload });
    }
  };

const mapMediaLibraryItemsToState = (media: MediaItem[]): MediaTypePostEditorState[] =>
  media.map(item => ({
    loading: false,
    url: item.url || '',
    id: item.id,
    gif: item.isGif,
    thumbnailUrl: item.thumbnailUrl || '',
    status: MediaUploadStatus.COMPLETED,
    type: item.type,
    proportions: item.proportion || getMediaProportion(item.width, item.height),
    width: item.width,
    height: item.height,
    length: item.length,
    transcoded: !!item.transcoded,
    filesize: item.fileSize,
    externalUpload: false
  }));

const mapMediaUploadItemToState = (item: MediaItemUpload): MediaTypePostEditorState => ({
  loading: true,
  url: item.url || '',
  id: item.id,
  gif: item.isGif,
  thumbnailUrl: item.file?.preview || '',
  status: MediaUploadStatus.PENDING,
  type: item.type,
  proportions: item.proportion || getMediaProportion(item.width, item.height),
  width: item.width,
  height: item.height,
  length: item.length,
  transcoded: false,
  filesize: item.file?.fileSize || 0,
  externalUpload: false
});

const getMediaIndex = (
  id: PostEditorMediaRemoveAction['payload']['id'],
  type: PostEditorMediaRemoveAction['payload']['type'],
  media: MediaTypePostEditorState[]
): number => {
  let index = -1;
  if (id) {
    index = media.findIndex(value => value.id === id && value.type === type);
  }
  return index;
};

const getVariationsData = (
  currentPost: GroupPostAttributesType,
  media: MediaTypePostEditorState[]
): Omit<VariationMeta, 'prevPostedCount' | 'currentVariationIndex'> => {
  const {
    varyMedia,
    variationMeta: { currentVariationIndex }
  } = currentPost.content;
  const { total, mediaVariationIndex, textVariationIndex } = calculateVariations(
    currentPost.content.texts,
    media,
    varyMedia,
    currentVariationIndex
  );
  return { total, mediaVariationIndex, textVariationIndex };
};

// get new Media function returns a new state portion of media that filter transformedMedia removing possible
// duplicates and merge it with current media in the state
const getMergedMediaStateAndRemoveDuplicates = (
  currentMediaFromState: MediaTypePostEditorState[],
  transformedMedia: MediaTypePostEditorState[]
): MediaTypePostEditorState[] => {
  const filtered = transformedMedia.filter(element => !currentMediaFromState.some(some => some.id === element.id));
  return [...currentMediaFromState, ...filtered];
};

const callbackWithTransformedMediaMedia: (
  transformedMedia: MediaTypePostEditorState[]
) => OnStateUpdateCallbackFunction = transformedMedia => currentPost => {
  const { update: updateImmutable } = ImmutabilityContext;
  const newMedia = getMergedMediaStateAndRemoveDuplicates(currentPost.content.media, transformedMedia);
  const { total, mediaVariationIndex, textVariationIndex } = getVariationsData(currentPost, newMedia);
  return updateImmutable(currentPost, {
    content: {
      media: {
        $set: newMedia
      },
      variationMeta: {
        total: { $set: total },
        mediaVariationIndex: { $set: mediaVariationIndex },
        textVariationIndex: { $set: textVariationIndex }
      }
    }
  });
};

const errorKeysList: PostEditorErrorKeys[] = [
  'facebookContentRequired',
  'facebookMediaLimit',
  'finishUpload',
  'googleMediaLimit',
  'googleMinMedia',
  'instagramMediaLimit',
  'linkedinMediaLimit',
  'mediaRequired',
  'noGifsAllowed',
  'noImagesAllowed',
  'noMixedMedia',
  'noVideosAllowed',
  'pinterestMediaLimit',
  'preventAudioOnlyFiles',
  'profileRequired',
  'scheduleTextOrMediaVariations',
  'tikTokMediaLimit',
  'tikTokVideoLength',
  'tikTokVideoSize',
  'twitterMediaLimit',
  'videoNotTranscoded'
];
export const mediaAddFromLibReducer: PostEditorReducerFunction<PostEditorMediaAddFromLibAction['payload']> = (
  state,
  payload
) => {
  const { postGroupIndex, customizeIndex, media } = payload;
  const transformedMedia: MediaTypePostEditorState[] = mapMediaLibraryItemsToState(media);
  const callback = callbackWithTransformedMediaMedia(transformedMedia);
  const result = postGroupStateUpdater(state, postGroupIndex, customizeIndex, 'content', callback);
  return validatorStateUpdater(result, postGroupIndex, ErrorMessageSeverity.warning, errorKeysList);
};

export const mediaFailedUploadReducer: PostEditorReducerFunction<PostEditorMediaFailedUploadAction['payload']> = (
  state,
  payload
) => {
  const { id, type } = payload;
  const callback: OnStateUpdateCallbackFunction = currentPost => {
    const { update: updateImmutable } = ImmutabilityContext;
    const mediaItems = currentPost.content.media.flatMap(item =>
      item.loading && item.type === type && item.id === id ? [] : item
    );
    const { total, mediaVariationIndex, textVariationIndex } = getVariationsData(currentPost, mediaItems);
    return updateImmutable(currentPost, {
      content: {
        media: {
          $set: mediaItems
        },
        variationMeta: {
          total: { $set: total },
          mediaVariationIndex: { $set: mediaVariationIndex },
          textVariationIndex: { $set: textVariationIndex }
        }
      }
    });
  };
  return postGroupCompleteUpdater(state, callback);
};

export const mediaRemoveReducer: PostEditorReducerFunction<PostEditorMediaRemoveAction['payload']> = (
  state,
  payload
) => {
  const { update: updateImmutable } = ImmutabilityContext;
  const { postGroupIndex, customizeIndex, id, type } = payload;
  const callback: OnStateUpdateCallbackFunction = currentPost => {
    const index = getMediaIndex(id, type, currentPost.content.media);
    if (index < 0) return currentPost;
    const result = updateImmutable(currentPost, {
      content: {
        media: {
          $splice: [[index, 1]]
        }
      }
    });
    const { total, mediaVariationIndex, textVariationIndex } = getVariationsData(result, result.content.media);
    return updateImmutable(result, {
      content: {
        variationMeta: {
          total: { $set: total },
          mediaVariationIndex: { $set: mediaVariationIndex },
          textVariationIndex: { $set: textVariationIndex }
        }
      }
    });
  };
  const result = postGroupStateUpdater(state, postGroupIndex, customizeIndex, 'content', callback);
  return validatorStateUpdater(result, postGroupIndex, ErrorMessageSeverity.warning, errorKeysList);
};

export const mediaReorderReducer: PostEditorReducerFunction<PostEditorMediaReorderAction['payload']> = (
  state,
  payload
) => {
  const { update: updateImmutable } = ImmutabilityContext;
  const { customizeIndex, postGroupIndex, reorderIndexes } = payload;
  const { dragIndex, hoverIndex } = reorderIndexes;
  const callback: OnStateUpdateCallbackFunction = currentPost =>
    updateImmutable(currentPost, {
      content: {
        media: {
          $apply: (media: MediaTypePostEditorState[]) =>
            updateImmutable(media, {
              $splice: [
                [dragIndex, 1],
                [hoverIndex, 0, media[dragIndex]]
              ]
            })
        }
      }
    });
  return postGroupStateUpdater(state, postGroupIndex, customizeIndex, 'content', callback);
};

const checkSubset = (parentArray: string[], subsetArray: string[]) => subsetArray.every(el => parentArray.includes(el));

const isPhotoGuard = (
  value:
    | BulkAddExternalPhotosMutation['bulkAddExternalPhotos'][number]['media']
    | BulkCompleteMediaUploadsMutation['bulkCompleteMediaUploads'][number]['media']
): value is Photo => {
  if (!value) return false;
  const keys = Object.keys(value);
  return checkSubset(keys, ['gif', 'filesize', 'filename']);
};

type MapFinishMediaOverloads = {
  (
    type: 'fromUpload',
    current: MediaTypePostEditorState[],
    media: MediaItemUpload,
    currentPost: GroupPostAttributesType
  ): GroupPostAttributesType;
  (
    type: 'fromUrl',
    current: MediaTypePostEditorState[],
    media: MediaTypePostEditorState,
    currentPost: GroupPostAttributesType
  ): GroupPostAttributesType;
};

const isMediaItemUploadGuard = (value: MediaItemUpload | MediaTypePostEditorState): value is MediaItemUpload => {
  const keys = Object.keys(value);
  return checkSubset(keys, ['completedMediaItem', 'proportion']);
};

const isMediaTypePostEditorStateGuard = (
  value: MediaItemUpload | MediaTypePostEditorState
): value is MediaTypePostEditorState => {
  const keys = Object.keys(value);
  return (
    checkSubset(keys, ['loading', 'thumbnailUrl', 'proportions', 'filesize']) && !keys.includes('completedMediaItem')
  );
};

const mapFinishedMedia: MapFinishMediaOverloads = (type, current, media, currentPost) => {
  const { update: updateImmutable } = ImmutabilityContext;
  const mappedItems = current.map((item: MediaTypePostEditorState) => {
    switch (type) {
      case 'fromUrl': {
        const found = item.id === media.id;
        if (item.loading && item.type === media.type && found && isMediaTypePostEditorStateGuard(media)) {
          return {
            ...item,
            status: media.status,
            loading: media.loading,
            thumbnailUrl: media.thumbnailUrl,
            width: media.width,
            height: media.height,
            proportions: media.proportions,
            filesize: media.filesize
          };
        } else {
          return item;
        }
      }
      default:
      case 'fromUpload': {
        if (!isMediaItemUploadGuard(media)) return item;
        const { completedMediaItem, proportion } = media;
        const found = item.id === media.id || item.id === completedMediaItem?.id;
        const isGif = isPhotoGuard(completedMediaItem) ? completedMediaItem.gif : item.gif;
        if (item.loading && item.type === media.type && found) {
          return {
            ...item,
            gif: isGif,
            status: media.status,
            loading: completedMediaItem ? false : item.loading,
            id: completedMediaItem ? completedMediaItem.id : item.id,
            thumbnailUrl:
              completedMediaItem && completedMediaItem.thumbnailUrl
                ? completedMediaItem.thumbnailUrl
                : item.thumbnailUrl,
            url: completedMediaItem && completedMediaItem.url ? completedMediaItem.url : item.url,
            proportions: proportion ?? item.proportions,
            filesize: completedMediaItem && completedMediaItem.filesize ? completedMediaItem.filesize : item.filesize,
            width: completedMediaItem && completedMediaItem.width ? completedMediaItem.width : item.width,
            height: completedMediaItem && completedMediaItem.height ? completedMediaItem.height : item.height
          };
        } else {
          return item;
        }
      }
    }
  });
  const map = new Map();
  for (const element of mappedItems) {
    map.set(element['id'], element);
  }
  const filteredData: MediaTypePostEditorState[] = [];
  map.forEach(value => {
    filteredData.push(value);
  });

  const result = updateImmutable(currentPost, {
    content: {
      media: {
        $set: filteredData
      }
    }
  });
  const { total, mediaVariationIndex, textVariationIndex } = getVariationsData(result, result.content.media);
  return updateImmutable(result, {
    content: {
      variationMeta: {
        total: { $set: total },
        mediaVariationIndex: { $set: mediaVariationIndex },
        textVariationIndex: { $set: textVariationIndex }
      }
    }
  });
};

export const mediaUpdateReducer: PostEditorReducerFunction<PostEditorMediaUpdateAction['payload']> = (
  state,
  payload
) => {
  const { update: updateImmutable } = ImmutabilityContext;
  const { media } = payload;
  const { completedMediaItem } = media;
  const callback: OnStateUpdateCallbackFunction = currentPost =>
    mapFinishedMedia('fromUpload', currentPost.content.media, media, currentPost);
  const result = postGroupCompleteUpdater(state, callback);
  // if we don't get the completed item, we don't want to run the validation since upload failed
  return completedMediaItem
    ? updateImmutable(result, {
        postGroups: {
          $apply: (postGroups: PostGroupsType[]) =>
            postGroups.map(group =>
              updateImmutable(group, {
                errors: {
                  $apply: (errors: PostEditorErrors) => {
                    const errorResult = validator(group, ErrorMessageSeverity.warning, errors, errorKeysList);
                    return updateImmutable(errors, {
                      $errorMerge: errorResult
                    });
                  }
                }
              })
            )
        }
      })
    : result;
};

export const mediaUpdateDeferredUploadedReducer: PostEditorReducerFunction<
  PostEditorMediaUpdateDeferredLoadedAction['payload']
> = (state, payload) => {
  const { media, postGroupIndex } = payload;
  const callback: OnStateUpdateCallbackFunction = currentPost =>
    mapFinishedMedia('fromUrl', currentPost.content.media, media, currentPost);
  const result = postGroupStateUpdater(state, postGroupIndex, undefined, 'content', callback);
  return validatorStateUpdater(result, postGroupIndex, ErrorMessageSeverity.warning, errorKeysList);
};

export const mediaAddFromUploadReducer: PostEditorReducerFunction<PostEditorMediaAddFromUploadAction['payload']> = (
  state,
  payload
) => {
  const { postGroupIndex, customizeIndex, media } = payload;
  const callback = callbackWithTransformedMediaMedia([media]);
  const result = postGroupStateUpdater(state, postGroupIndex, customizeIndex, 'content', callback);
  return validatorStateUpdater(result, postGroupIndex, ErrorMessageSeverity.warning, errorKeysList);
};

export const mediaSetVaryReducer: PostEditorReducerFunction<PostEditorMediaVaryAction['payload']> = (
  state,
  payload
) => {
  const { update: updateImmutable } = ImmutabilityContext;
  const { postGroupIndex, varyMedia, customizeIndex } = payload;
  const callback: OnStateUpdateCallbackFunction = currentPost => {
    const {
      variationMeta: { currentVariationIndex }
    } = currentPost.content;
    const { total, mediaVariationIndex, textVariationIndex } = calculateVariations(
      currentPost.content.texts,
      currentPost.content.media,
      varyMedia,
      currentVariationIndex
    );
    return updateImmutable(currentPost, {
      content: {
        varyMedia: {
          $set: varyMedia
        },
        variationMeta: {
          total: { $set: total },
          mediaVariationIndex: { $set: mediaVariationIndex },
          textVariationIndex: { $set: textVariationIndex }
        }
      }
    });
  };
  const result = postGroupStateUpdater(state, postGroupIndex, customizeIndex, 'content', callback);
  return validatorStateUpdater(result, postGroupIndex, ErrorMessageSeverity.warning, errorKeysList);
};
